<template>
  <VueTagsInput
    ref="input"
    v-model="search"
    :tags="tags"
    :autocomplete-min-length="0"
    :autocomplete-items="filteredManagers"
    :add-only-from-autocomplete="true"
    placeholder="Account managers..."
    class="managers-tags-input"
    v-on="$listeners"
    @tags-changed="onTagsChanged"
  />
</template>

<script>
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import VueTagsInput from '@johmun/vue-tags-input'
import { matchesQuery } from '@/utils/autocompleteFilter'

@Component({
  components: {
    VueTagsInput
  },
  computed: {
    ...mapGetters(['currentMerchant'])
  }
})
export default class MultiUserSelect extends Vue {
  @Prop({ type: Array, default: () => [] }) value
  @Prop({ type: Boolean, default: true }) includeUnassigned

  search = ''
  tags = []

  @Watch('value', { deep: true })
  onValueChange(val) {
    const pkids = (val || [])
      .map(m => m?.pkid)
      .filter(v => v !== undefined && v !== null)
      .sort()
    const currentPkids = this.selectedPkids.slice().sort()
    if (
      pkids.length === currentPkids.length &&
      pkids.every((p, i) => p === currentPkids[i])
    ) {
      return
    }
    if (val?.length && Array.isArray(val)) {
      this.setValue(val)
    } else if (!val?.length) {
      this.tags = []
    }
  }

  get accountManagers() {
    const managers = this.currentMerchant?.account_managers || []
    if (!this.includeUnassigned) return managers

    return [{ pkid: -1, first_name: 'Unassigned', last_name: '' }, ...managers]
  }

  get selectedPkids() {
    return this.tags
      .map(t => t.value?.pkid ?? t.pkid)
      .filter(v => v !== undefined && v !== null)
  }

  get filteredManagers() {
    const managers = this.accountManagers
    const selected = this.selectedPkids

    // Match on the rendered "First Last" string, so a query spanning both names
    // hits and the synthetic Unassigned row is searchable like any other.
    return managers
      .filter(m => !selected.includes(m.pkid))
      .map(m => ({
        text: `${m.first_name || ''} ${m.last_name || ''}`.trim(),
        value: m
      }))
      .filter(item => matchesQuery(this.search, item.text))
  }

  clear() {
    this.search = ''
    this.tags = []
    this.emitChange()
  }

  onTagsChanged(newTags) {
    this.tags = newTags
    this.emitChange()
  }

  emitChange() {
    const value = this.tags.map(t => t.value || t).filter(Boolean)
    this.$emit('change', value)
    this.$emit('input', value)
  }

  setValue(items) {
    if (!items?.length) {
      this.tags = []

      return
    }
    const managers = this.accountManagers
    this.tags = items.map(manager => {
      const full = managers.find(m => m.pkid === (manager?.pkid ?? manager))
      const obj = full || manager
      const text = `${obj.first_name || ''} ${obj.last_name || ''}`.trim()

      return { text, value: obj }
    })
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/variables';

.managers-tags-input {
  flex: 1;
  min-width: 0;
}
.managers-tags-input ::v-deep .ti-tag.ti-valid {
  &:not(.ti-deletion-mark) {
    background-color: $button-blue;
    border-color: $button-blue;
    color: #fff;
  }
}
.managers-tags-input ::v-deep .ti-autocomplete {
  max-height: 150px;
  background-color: #fff !important;
  border: 1px solid #ced4da;
  border-top: none;
  color: #212529;
  .ti-item {
    color: #212529;
    > div {
      padding: 6px 10px;
    }
    &.ti-selected-item {
      background-color: $button-blue;
      color: #fff;
    }
  }
}
</style>
